/**
 * Calendar-mode "next change" label for a relay card.
 *
 * Calendar-managed sites don't render the schedule row list on the
 * dashboard (the calendar view owns intent); this one-line label reads
 * the same resolved hardware spans the timeline strip above it draws and
 * surfaces the nearest upcoming on/off transition, so the card still
 * answers "what happens next" without duplicating ScheduleList.
 *
 * Contains:
 * - RelayScheduleNextChangeLabel — the rendered label
 */
import { Typography } from '@mui/material';
import { addMinutes, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatTime } from '@sophocles/utils/formatUtils';
import {
  findNextScheduleChange,
  minutesOfDay,
  resolveActiveScheduleSpans,
} from '@sophocles/views/sites/components/RelayCards/scheduleTimelineSpans';

/**
 * @param {{
 *   schedules: object[],
 *   user: object,
 *   nextSunrise: Date|null,
 *   nextSunset: Date|null,
 *   siteTimeZone: string,
 * }} props
 */
function RelayScheduleNextChangeLabel({ schedules, user, nextSunrise, nextSunset, siteTimeZone }) {
  const { t } = useTranslation();

  const scheduleSpans = resolveActiveScheduleSpans(schedules, nextSunrise, nextSunset);
  const zonedNow = toZonedTime(new Date(), siteTimeZone ?? 'UTC');
  const nextChange = findNextScheduleChange(scheduleSpans, minutesOfDay(zonedNow));

  const label =
    nextChange == null
      ? t('sites.noSchedules', 'No Schedules')
      : nextChange.isOn
        ? t('sites.relayNextOn', 'On at {{time}}', {
            time: formatTime(addMinutes(startOfDay(zonedNow), nextChange.minutes), user),
          })
        : t('sites.relayNextOff', 'Off at {{time}}', {
            time: formatTime(addMinutes(startOfDay(zonedNow), nextChange.minutes), user),
          });

  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', px: 2, pb: 1.5 }}>
      {label}
    </Typography>
  );
}

export default RelayScheduleNextChangeLabel;
